/**
 * 웹뷰 쪽 MCP 브리지(§6.6-3, T-6.04). Rust가 보낸 `mcp:request`를 read-model로 계산해 `mcp_bridge_reply`로 돌려준다.
 *
 * - 스토어 하이드레이션은 모듈을 불러올 때 동기로 끝난다. 앱이 한 번 그려진 뒤 준비 신호를 보낸다(ADV-D07).
 * - 페이지가 내려가면 준비를 거둔다. Rust도 페이지 로드 시작 때 대기 요청을 끝낸다(ADV-D10).
 * - 계산 전에 한 틱 양보해 사용자 입력이 먼저 처리되게 한다(ADV-I06).
 */
import { todayISO } from "@/engine/date";
import { catalogSnapshot } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { runTool, snapshotFromCatalog, type McpSnapshot } from "./mcp-read-model";
import { invoke, isDesktop, listen } from "./runtime";

interface RelayRequest {
  requestId: string;
  tool: string;
  args: Record<string, unknown>;
}

/** 지금 스토어의 확정된 값으로 스냅샷을 만든다. */
export function currentSnapshot(): McpSnapshot {
  const editor = useProjectStore.getState();
  return snapshotFromCatalog(catalogSnapshot(), { activeId: editor.activeId, project: editor.project, reports: editor.reports });
}

async function handle(req: RelayRequest): Promise<void> {
  await new Promise((r) => setTimeout(r, 0));
  let reply: { requestId: string; ok: boolean; data?: unknown; error?: { code: string; message: string } };
  try {
    const out = runTool(req.tool, req.args, currentSnapshot(), { today: todayISO() });
    reply = out.ok ? { requestId: req.requestId, ok: true, data: out.data } : { requestId: req.requestId, ok: false, error: out.error };
  } catch {
    reply = { requestId: req.requestId, ok: false, error: { code: "INTERNAL", message: "읽기 모델에서 오류가 났습니다" } };
  }
  // 타임아웃 뒤에 도착한 응답은 Rust가 버리고 기록만 한다. 여기서는 오류를 무시한다.
  await invoke("mcp_bridge_reply", { reply }).catch(() => {});
}

let started: Promise<void> | null = null;

/**
 * 브리지를 켠다. 페이지 수명 동안 한 번만 켜고(개발 모드 StrictMode의 이중 effect에도 준비 신호가 뒤집히지 않게),
 * 페이지가 내려갈 때(`pagehide`)만 준비를 거둔다. 웹에서는 아무 일도 하지 않는다.
 */
export function startMcpBridge(): Promise<void> {
  if (!isDesktop()) return Promise.resolve();
  started ??= (async () => {
    await listen<RelayRequest>("mcp:request", (req) => void handle(req));
    window.addEventListener("pagehide", () => void invoke("mcp_bridge_set_ready", { ready: false }).catch(() => {}), { once: true });
    await invoke("mcp_bridge_set_ready", { ready: true });
  })().catch((e) => {
    started = null;
    throw e;
  });
  return started;
}
